import { supabase } from '../supabaseClient';
import { describeError } from '../errorMessage';
import { MELDUNG_ERWEITERUNG_FEHLT, istFehlendeErweiterung } from './sifaService';
import { SifaRpcFehler, fehlerCode, mapUnterweisungPublic, rpcAntwortObjekt, rpcFeld } from './sifaPublicService';
import type { TeilnahmeAntwort, TestAuswertung, TestLink, UnterweisungTestPublic } from './types';

/**
 * Test-Links (Nr. 143/144, docs/sifa-ausbau2-plan.md §0.1): einen Entwurf —
 * Kunden-Unterweisung oder Vorlage — am PC oder Handy durchklicken, ohne
 * dass irgendetwas gespeichert wird. Keine Teilnahme, kein Zertifikat, keine
 * Abrechnung, keine Mitarbeiter: die Auswertung (`sifa_testlink_auswerten`)
 * rechnet nur und schreibt nichts.
 *
 * Die Tabelle `sifa_testlinks` ist fuer Client-Rollen gesperrt; alles laeuft
 * ueber Definer-RPCs. Der Token gilt 24 Stunden (setzt die Datenbank).
 *
 * FAIL-OPEN: ohne Nr. 144 liefert ladeTestPublic null („Link ungueltig"),
 * Anlegen und Auswerten werfen Klartext.
 */

type Art = 'unterweisung' | 'vorlage';
const ARTEN: readonly Art[] = ['unterweisung', 'vorlage'];

const str = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v));
const num = (v: unknown, fallback: number): number => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
};
/** Rest-Zaehler der Datenbank; fehlt er (aeltere Fassung), null statt einer erfundenen Zahl. */
const restOderNull = (v: unknown): number | null => (v == null ? null : Math.max(0, num(v, 0)));

/** 54000 = program_limit_exceeded — die Datenbank bremst (Test-Kontingent, zu viele Test-Links). */
const BREMSE = '54000';

/**
 * Das Kontingent eines Test-Links ist aufgebraucht (20 Auswertungen, Nr. 144).
 * Eigener Typ, damit die Testseite statt „erneut versuchen" auf einen neuen
 * Test-Link verweist.
 */
export class TestKontingentErschoepftError extends SifaRpcFehler {
  constructor() {
    super('Test-Kontingent erschöpft (20 Auswertungen je Test-Link) — bitte im Editor einen neuen Test-Link erzeugen.', BREMSE);
    this.name = 'TestKontingentErschoepftError';
  }
}

/**
 * Legt einen Test-Link an (RPC `sifa_testlink_anlegen`). Die Datenbank prueft
 * den Zugriff (Projekt bzw. Firma der Vorlage; Freelancer nie) und raeumt
 * dabei abgelaufene Test-Links ab.
 */
export const legeTestLinkAn = async (art: Art, id: string): Promise<TestLink> => {
  if (!ARTEN.includes(art)) throw new Error(`Unbekannte Art „${String(art)}" für einen Test-Link.`);
  const rid = (id || '').trim();
  if (!rid) throw new Error('Ohne Unterweisung lässt sich kein Test-Link anlegen.');
  const { data, error } = await supabase.rpc('sifa_testlink_anlegen', { p_art: art, p_id: rid });
  if (error) {
    if (istFehlendeErweiterung(error)) throw new Error(MELDUNG_ERWEITERUNG_FEHLT);
    if (fehlerCode(error) === '42501') throw new Error(`Für diese Unterweisung fehlt die Berechtigung zum Testen. (${describeError(error)})`);
    // Bremse der Datenbank gegen massenhaft angelegte Test-Links.
    if (fehlerCode(error) === BREMSE) {
      throw new Error('Zu viele Test-Links in kurzer Zeit — bitte einen bestehenden weiterverwenden oder später erneut versuchen.');
    }
    throw new Error(describeError(error));
  }
  const row = rpcAntwortObjekt(data);
  const token = str(row ? rpcFeld(row, 'token') : '').trim();
  if (!row || !token) throw new Error('Der Test-Link konnte nicht angelegt werden.');
  return { token, gueltigBis: str(rpcFeld(row, 'gueltigBis', 'gueltig_bis')) };
};

/** Ursprung der App fuer Links — im Browser die aktuelle Adresse, sonst leer. */
const hierOrt = (): { origin: string; pathname: string } =>
  typeof window !== 'undefined' && window.location
    ? { origin: window.location.origin, pathname: window.location.pathname }
    : { origin: '', pathname: '/' };

/** `<origin>/#/unterweisung-test/<token>` — fuer „Oeffnen" und den QR-Code; `ort` nur fuer Tests. */
export const testLinkUrl = (token: string, ort?: { origin: string; pathname?: string }): string => {
  const o = ort ?? hierOrt();
  return `${o.origin}${o.pathname || '/'}#/unterweisung-test/${encodeURIComponent((token || '').trim())}`;
};

/**
 * Der Kurs zum Test-Link (RPC `sifa_testlink_public`) — wie die echte
 * Teilnehmerseite, Fragen OHNE Richtig-Flags. null = abgelaufen, unbekannt
 * oder Nr. 144 fehlt.
 */
export const ladeTestPublic = async (token: string): Promise<UnterweisungTestPublic | null> => {
  const t = (token || '').trim();
  if (!t) return null;
  try {
    const { data, error } = await supabase.rpc('sifa_testlink_public', { p_token: t });
    if (error) throw error;
    const row = rpcAntwortObjekt(data);
    if (!row) return null;
    return {
      ...mapUnterweisungPublic(row),
      test: true,
      gueltigBis: str(rpcFeld(row, 'gueltigBis', 'gueltig_bis')),
      auswertungenRest: restOderNull(rpcFeld(row, 'auswertungenRest', 'auswertungen_rest')),
    };
  } catch (e) {
    if (istFehlendeErweiterung(e)) return null;
    throw new SifaRpcFehler(describeError(e), fehlerCode(e));
  }
};

/**
 * Wertet einen Testlauf aus (RPC `sifa_testlink_auswerten`) — dieselbe Regel
 * wie beim echten Abschluss, aber ohne eine Zeile zu schreiben. Die
 * Richtig-Flags bleiben in der Datenbank; zurueck kommen nur Punkte,
 * Bestehen und der Rest des Kontingents — welche Fragen falsch waren, sagt
 * die Datenbank bewusst nicht (sonst liesse sich die Loesung erproben).
 * Aufgebraucht (54000) → TestKontingentErschoepftError.
 */
export const werteTestAus = async (token: string, antworten: TeilnahmeAntwort[]): Promise<TestAuswertung> => {
  const t = (token || '').trim();
  if (!t) throw new SifaRpcFehler('Dieser Test-Link ist nicht gültig.', null);
  const { data, error } = await supabase.rpc('sifa_testlink_auswerten', {
    p_token: t,
    p_antworten: (antworten ?? []).map((a) => ({ frageId: a.frageId, antwortIds: a.antwortIds ?? [] })),
  });
  if (error) {
    if (istFehlendeErweiterung(error)) throw new SifaRpcFehler(MELDUNG_ERWEITERUNG_FEHLT, fehlerCode(error));
    if (fehlerCode(error) === BREMSE) throw new TestKontingentErschoepftError();
    throw new SifaRpcFehler(describeError(error), fehlerCode(error));
  }
  const row = rpcAntwortObjekt(data);
  if (!row) throw new SifaRpcFehler('Der Test-Link ist abgelaufen — bitte im Editor einen neuen erzeugen.', null);
  return {
    punkte: num(row.punkte, 0),
    punkteMax: num(rpcFeld(row, 'punkteMax', 'punkte_max'), 0),
    bestanden: row.bestanden === true,
    bestehenProzent: num(rpcFeld(row, 'bestehenProzent', 'bestehen_prozent'), 80),
    auswertungenRest: restOderNull(rpcFeld(row, 'auswertungenRest', 'auswertungen_rest')),
  };
};
